import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { type ReactNode, useRef, useState } from "react";
import { Popover } from "react-aria-components";

import {
  MDSActionMenuDropdown,
  MDSButton,
  MDSButtonSize,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSIcon,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSIconKind,
  MDSMenuContentItemKind,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { AgentLinkButton } from "@/pages/agent/components/agent-link-button";
import { AgentTaskStateIcon } from "@/pages/agent/components/agent-task-state-icon";
import { useAgentIntentGraph } from "@/pages/agent/intent-graph/use-agent-intent-graph";
import { formatAgentDate } from "@/pages/agent/utils/agent-date.utils";
import { useAppStore } from "@/store";
import {
  isClosedAssistantTaskStatus,
  normalizeAssistantTaskStatus,
} from "@/store/assistant/assistant-task-status";
import { AssistantTaskStatus } from "@/store/assistant/types";

interface NoteConnectedWorkPopoverProps {
  isReviewing?: boolean;
  noteId: string;
  onRequestReview?: () => void;
}

const getTaskToggleLabel = ({ status, title }: { status: string; title: string }) => {
  const normalizedStatus = normalizeAssistantTaskStatus({ status });

  if (normalizedStatus === AssistantTaskStatus.Canceled) return `Reopen ${title}`;
  if (normalizedStatus === AssistantTaskStatus.Completed) return `Mark ${title} incomplete`;

  return `Mark ${title} complete`;
};

export const NoteConnectedWorkPopover = observer<NoteConnectedWorkPopoverProps>(
  function NoteConnectedWorkPopover({ isReviewing = false, noteId, onRequestReview }) {
    const { store } = useAppStore();
    const graph = useAgentIntentGraph();
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [isOpen, setIsOpen] = useState(false);

    const taskIds = [
      ...new Set(
        store.assistantTaskNoteReferences.currentAssistantModels
          .filter(reference => reference.modelData.note_id === noteId)
          .map(reference => reference.modelData.task_id)
      ),
    ];
    const tasks = taskIds.flatMap(taskId => {
      const task = store.assistantTasks.getCurrentAssistantModelById(taskId);
      return task ? [task] : [];
    });
    const informationItem = graph.data?.information_items?.find(
      item => item.kind === "note" && item.locator === noteId
    );

    const hasConnectedWork = tasks.length > 0 || Boolean(informationItem);
    const reviewMenuItems = onRequestReview
      ? [
          {
            id: "review-note-again",
            kind: MDSMenuContentItemKind.Item,
            label: isReviewing ? "Reviewing note…" : "Review this note again",
            icon: MDSIconKind.Redo,
            isDisabled: isReviewing,
            onClick: onRequestReview,
          },
        ]
      : [];

    if (!hasConnectedWork) {
      if (!onRequestReview) return null;

      return (
        <ConnectedWorkSlot data-test-id="note-connected-work-top-bar-slot">
          <MDSActionMenuDropdown contentList={reviewMenuItems} placement="bottom end">
            <MDSIconButton
              aria-label="More note review actions"
              iconKind={MDSIconKind.More}
              size={MDSIconButtonSize.Medium}
              variant={MDSIconButtonVariant.Transparent}
            />
          </MDSActionMenuDropdown>
        </ConnectedWorkSlot>
      );
    }

    const paths = tasks.map(task => {
      const project = task.projectId
        ? store.assistantProjects.getCurrentAssistantModelById(task.projectId)
        : undefined;
      const goalId = project
        ? graph.data?.project_goal_links?.find(link => link.project_id === project.id)?.goal_id
        : undefined;
      const goal = goalId ? graph.data?.goals?.find(item => item.id === goalId) : undefined;
      const intent = graph.data?.tasks?.find(item => item.id === task.id);
      const parentGoal = goal?.parent_goal_id
        ? graph.data?.goals?.find(item => item.id === goal.parent_goal_id)
        : undefined;
      const parentProjectId = project
        ? graph.data?.project_hierarchy_links?.find(link => link.project_id === project.id)
            ?.parent_project_id
        : undefined;
      const parentProject = parentProjectId
        ? store.assistantProjects.getCurrentAssistantModelById(parentProjectId)
        : undefined;
      const parentTask = intent?.parent_task_id
        ? store.assistantTasks.getCurrentAssistantModelById(intent.parent_task_id)
        : undefined;

      return {
        goal,
        intent,
        parentGoal,
        parentProject,
        parentTask,
        project,
        task,
      };
    });
    const connectedEntityCount = new Set(
      paths.flatMap(({ goal, project, task }) => [task.id, project?.id, goal?.id].filter(Boolean))
    ).size;
    const firstPath = paths.at(0);
    const lifecycle = informationItem?.lifecycle ?? "active";
    const lifecycleLabel = lifecycle === "archived" ? "Archived" : "Active";
    const openEntity = (open: () => void) => {
      setIsOpen(false);
      open();
    };

    const toggleTaskComplete = async ({ taskId }: { taskId: string }) => {
      const task = store.assistantTasks.getCurrentAssistantModelById(taskId);
      if (!task) return;

      const currentStatus = normalizeAssistantTaskStatus({
        status: task.status,
      });
      const nextStatus = isClosedAssistantTaskStatus({ status: currentStatus })
        ? AssistantTaskStatus.Pending
        : AssistantTaskStatus.Completed;

      await task.updateStatus({ status: nextStatus });
    };

    return (
      <ConnectedWorkSlot data-test-id="note-connected-work-top-bar-slot">
        <MDSButton
          ref={triggerRef}
          aria-expanded={isOpen}
          aria-label={`Connected work, ${connectedEntityCount} ${connectedEntityCount === 1 ? "item" : "items"}`}
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Chrome}
          onPress={() => {
            const nextIsOpen = !isOpen;
            setIsOpen(nextIsOpen);
            if (nextIsOpen) {
              void graph.refetch();
            }
          }}
        >
          <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.ChartNetwork} />
          Connected work
        </MDSButton>
        <StyledPopover
          isNonModal
          isOpen={isOpen}
          placement="bottom end"
          triggerRef={triggerRef}
          onOpenChange={setIsOpen}
        >
          <ConnectedWorkCard data-test-id="note-connected-work-card">
            <Header>
              <HeaderTitleRow>
                <MDSText
                  color={MDSTextColor.Primary}
                  variant={MDSTextVariant.HeaderS}
                  weight={MDSTextWeight.SemiBold}
                >
                  Connected work
                </MDSText>
                <HeaderActions>
                  <LifecycleStatus>
                    <LifecycleDot $archived={lifecycle === "archived"} aria-hidden="true" />
                    <MDSText
                      color={MDSTextColor.Secondary}
                      variant={MDSTextVariant.TextXs}
                      weight={MDSTextWeight.Medium}
                    >
                      {lifecycleLabel}
                    </MDSText>
                  </LifecycleStatus>
                  {reviewMenuItems.length > 0 ? (
                    <MDSActionMenuDropdown contentList={reviewMenuItems} placement="bottom end">
                      <MDSIconButton
                        aria-label="More Connected Work actions"
                        iconKind={MDSIconKind.More}
                        size={MDSIconButtonSize.XSmall}
                        variant={MDSIconButtonVariant.Transparent}
                      />
                    </MDSActionMenuDropdown>
                  ) : null}
                </HeaderActions>
              </HeaderTitleRow>
              <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextXs}>
                Information from this note
              </MDSText>
            </Header>
            {informationItem?.lifecycle_rationale ? (
              <LifecycleSummary>
                <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextXs}>
                  {informationItem.lifecycle_rationale}
                </MDSText>
              </LifecycleSummary>
            ) : null}
            <PathList>
              {paths.map(
                ({ goal, intent, parentGoal, parentProject, parentTask, project, task }) => (
                  <Path key={task.id}>
                    <TaskRow>
                      <TaskStateButton
                        aria-label={getTaskToggleLabel({
                          status: task.status,
                          title: task.title,
                        })}
                        iconKind={() => <AgentTaskStateIcon size={18} status={task.status} />}
                        size={MDSIconButtonSize.XSmall}
                        variant={MDSIconButtonVariant.Transparent}
                        onPress={() => void toggleTaskComplete({ taskId: task.id })}
                      />
                      <TaskContent>
                        <TaskLink
                          truncate
                          onClick={() =>
                            openEntity(() =>
                              store.agentOverlay.openTask({
                                taskId: task.id,
                              })
                            )
                          }
                        >
                          {task.title}
                        </TaskLink>
                        {intent?.next_wake_at ? (
                          <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextXs}>
                            Wakes{" "}
                            {formatAgentDate({
                              value: intent.next_wake_at,
                              includeTime: true,
                            })}
                          </MDSText>
                        ) : null}
                      </TaskContent>
                    </TaskRow>
                    {parentTask || project || goal ? (
                      <ContextList>
                        {parentTask ? (
                          <ContextRow>
                            <RelationshipLabel>Subtask of</RelationshipLabel>
                            <ContextLink
                              truncate
                              onClick={() =>
                                openEntity(() =>
                                  store.agentOverlay.openTask({
                                    taskId: parentTask.id,
                                  })
                                )
                              }
                            >
                              {parentTask.title}
                            </ContextLink>
                          </ContextRow>
                        ) : null}
                        {project ? (
                          <ContextRow>
                            <RelationshipLabel>Project</RelationshipLabel>
                            <ContextTrail>
                              {parentProject ? (
                                <>
                                  <ContextLink
                                    truncate
                                    onClick={() =>
                                      openEntity(() =>
                                        store.agentOverlay.openProject({
                                          projectId: parentProject.id,
                                        })
                                      )
                                    }
                                  >
                                    {parentProject.title}
                                  </ContextLink>
                                  <TrailSeparator aria-hidden="true">›</TrailSeparator>
                                </>
                              ) : null}
                              <ContextLink
                                truncate
                                onClick={() =>
                                  openEntity(() =>
                                    store.agentOverlay.openProject({
                                      projectId: project.id,
                                    })
                                  )
                                }
                              >
                                {project.title}
                              </ContextLink>
                            </ContextTrail>
                          </ContextRow>
                        ) : null}
                        {goal ? (
                          <ContextRow>
                            <RelationshipLabel>Goal</RelationshipLabel>
                            <ContextTrail>
                              {parentGoal ? (
                                <>
                                  <ContextLink
                                    truncate
                                    onClick={() =>
                                      openEntity(() =>
                                        store.agentOverlay.openGoal({
                                          goalId: parentGoal.id,
                                        })
                                      )
                                    }
                                  >
                                    {parentGoal.title}
                                  </ContextLink>
                                  <TrailSeparator aria-hidden="true">›</TrailSeparator>
                                </>
                              ) : null}
                              <ContextLink
                                truncate
                                onClick={() =>
                                  openEntity(() =>
                                    store.agentOverlay.openGoal({
                                      goalId: goal.id,
                                    })
                                  )
                                }
                              >
                                {goal.title}
                              </ContextLink>
                            </ContextTrail>
                          </ContextRow>
                        ) : null}
                      </ContextList>
                    ) : null}
                  </Path>
                )
              )}
            </PathList>
            {informationItem || firstPath ? (
              <Footer>
                <MDSButton
                  size={MDSButtonSize.Small}
                  variant={MDSButtonVariant.Emphasis}
                  onPress={() => {
                    if (informationItem) {
                      openEntity(() =>
                        store.agentOverlay.openInformationItem({
                          informationItemId: informationItem.id,
                        })
                      );
                      return;
                    }
                    if (firstPath) {
                      openEntity(() =>
                        store.agentOverlay.openTask({
                          taskId: firstPath.task.id,
                        })
                      );
                    }
                  }}
                >
                  <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.LightbulbOn} />
                  Open in Agent
                </MDSButton>
              </Footer>
            ) : null}
          </ConnectedWorkCard>
        </StyledPopover>
      </ConnectedWorkSlot>
    );
  }
);

const ConnectedWorkSlot = styled.span`
  display: inline-flex;
`;

const StyledPopover = styled(Popover)`
  width: min(420px, calc(100vw - 24px));
`;

const ConnectedWorkCard = styled.div`
  background-color: ${({ theme }) => theme.colors.surface.onboarding};
  border: 1px solid ${({ theme }) => theme.colors.border.chrome};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[5]}`};
`;

const HeaderTitleRow = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const HeaderActions = styled.div`
  align-items: center;
  display: inline-flex;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const LifecycleDot = styled.span<{ $archived: boolean }>`
  background-color: ${({ $archived, theme }) =>
    $archived ? theme.colors.text.tertiary : theme.colors.text.positive};
  border-radius: 50%;
  height: 7px;
  width: 7px;
`;

const LifecycleStatus = styled.div`
  align-items: center;
  display: inline-flex;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const PathList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  max-height: min(440px, 60vh);
  overflow-y: auto;
  padding: ${({ theme }) => `0 ${theme.spacing[3]} ${theme.spacing[3]}`};
`;

const LifecycleSummary = styled.div`
  padding: ${({ theme }) => `0 ${theme.spacing[5]} ${theme.spacing[3]}`};
`;

const Path = styled.div`
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[4]};
`;

const TaskRow = styled.div`
  align-items: flex-start;
  display: grid;
  gap: ${({ theme }) => theme.spacing[2]};
  grid-template-columns: 20px minmax(0, 1fr);
`;

const TaskStateButton = styled(MDSIconButton)`
  margin-top: -2px;
`;

const TaskContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;

const TaskLink = styled(AgentLinkButton)`
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.text.emphasis};
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;

const ContextList = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.border.surface};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  padding-top: ${({ theme }) => theme.spacing[3]};
`;

const ContextRow = styled.div`
  align-items: center;
  display: grid;
  gap: ${({ theme }) => theme.spacing[2]};
  grid-template-columns: 64px minmax(0, 1fr);

  > :first-of-type {
    white-space: nowrap;
  }
`;

const RelationshipLabel = ({ children }: { children: ReactNode }) => (
  <MDSText
    color={MDSTextColor.Secondary}
    variant={MDSTextVariant.TextXs}
    weight={MDSTextWeight.SemiBold}
  >
    {children}
  </MDSText>
);

const ContextTrail = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;

const ContextLink = styled(AgentLinkButton)`
  color: ${({ theme }) => theme.colors.text.emphasis};
  flex: 0 1 auto;
  min-width: 0;
  text-decoration-color: transparent;

  &:hover {
    text-decoration-color: currentColor;
  }
`;

const TrailSeparator = styled.span`
  color: ${({ theme }) => theme.colors.text.tertiary};
  flex: 0 0 auto;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: ${({ theme }) => `0 ${theme.spacing[3]} ${theme.spacing[3]}`};
`;
